/**
 * Strips <quote> tags and markdown formatting for clean clipboard paste.
 *
 * Analysis output is LLM-generated and its shape can vary — a section's
 * title or body is occasionally missing entirely. All helpers below accept
 * `string | null | undefined` and coerce to an empty string so a malformed
 * section can never throw when the user copies results to the clipboard.
 */
function stripQuoteTags(text?: string | null): string {
  return (text ?? '').replace(/<\/?quote[^>]*>/gi, '');
}

/**
 * Strips markdown formatting (bold, italic, headers, links, etc.)
 */
function stripMarkdown(text?: string | null): string {
  return (text ?? '')
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1') // bold alt
    .replace(/_(.+?)_/g, '$1') // italic alt
    .replace(/#{1,6}\s?/g, '') // headers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/```[\s\S]*?```/g, '') // code blocks
    .replace(/>\s?/gm, '') // blockquotes
    .trim();
}

/**
 * Cleans a single piece of text (quote tags + markdown), tolerating
 * missing/null/undefined input.
 */
function cleanText(text?: string | null): string {
  return stripMarkdown(stripQuoteTags(text));
}

/**
 * Formats a single section (title + body) for clipboard.
 *
 * Either field may be missing. Empty fields are dropped rather than
 * leaving a blank line — e.g. a title-less section renders as just its
 * body, not `"\n<body>"`.
 */
export function formatSectionForClipboard(title?: string | null, body?: string | null): string {
  const cleanTitle = cleanText(title);
  const cleanBody = cleanText(body);
  return [cleanTitle, cleanBody].filter(Boolean).join('\n');
}

type ClipboardSection = { title?: string | null; body?: string | null };

/**
 * Formats an array of sections (e.g., themes, patterns) for clipboard.
 *
 * Tolerates a missing/null `sections` argument and null elements within the
 * array (both observed as real-world analysis-output shapes, not just
 * missing fields on an otherwise-present section object).
 *
 * Sections with a missing title and/or body are still included (using
 * whichever field is present); sections that resolve to no content at all
 * are dropped. Numbering is applied AFTER dropping empty sections, so a
 * section removed from the middle of the list never leaves a gap in the
 * numbering (e.g. 1, 2, 3 — never 1, 3).
 */
export function formatSectionsForClipboard(
  sections?: Array<ClipboardSection | null> | null,
): string {
  const formattedSections = (sections ?? [])
    .filter((section): section is ClipboardSection => Boolean(section))
    .map((section) => formatSectionForClipboard(section.title, section.body))
    .filter(Boolean);

  return formattedSections.map((formatted, index) => `${index + 1}. ${formatted}`).join('\n\n');
}

/**
 * Formats a summary text for clipboard.
 */
export function formatSummaryForClipboard(summary?: string | null): string {
  return cleanText(summary);
}
